import React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { API_URL } from '../../config';
import { Button, Flex, Heading, LoadingOrbit, Text, BrowserStreamPanel, useToast } from '@chatsdet/ui';
import { useBrowserStream } from '../../hooks/useBrowserStream';

export function TestLoginMCPPreview() {
  const { propertyId, credentialId } = useParams({
    from: '/applications/$propertyId/credentials/$credentialId/mcp-preview',
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  const [error, setError] = React.useState<string | null>(null);
  const [streamId, setStreamId] = React.useState<string | null>(null);
  const [isStarting, setIsStarting] = React.useState(true);
  const { state } = useBrowserStream(streamId);

  const handleBack = React.useCallback(() => {
    navigate({
      to: '/applications/$propertyId',
      params: { propertyId },
    });
  }, [navigate, propertyId]);

  React.useEffect(() => {
    document.title = 'MCP Test Login';
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const start = async () => {
      try {
        const response = await fetch(
          `${API_URL}/org/properties/${propertyId}/credentials/${credentialId}/mcp-test/browser-stream`,
          {
            method: 'POST',
            credentials: 'include',
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          const message = text || `Failed to start MCP login (${response.status})`;
          setError(message);
          setIsStarting(false);
          toast.error(message);
          return;
        }

        const data = (await response.json()) as {
          ok?: boolean;
          streamId?: string;
          message?: string;
        };
        if (!data.ok || !data.streamId) {
          const message = data.message ?? 'Failed to start MCP login.';
          setError(message);
          setIsStarting(false);
          toast.error(message);
          return;
        }

        setStreamId(data.streamId);
      } catch (err) {
        if (controller.signal.aborted || cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        setIsStarting(false);
        toast.error(message);
      }
    };

    const timeoutId = window.setTimeout(() => {
      if (!cancelled) {
        void start();
      }
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [credentialId, propertyId, toast]);

  React.useEffect(() => {
    if (streamId) {
      setIsStarting(false);
    }
  }, [streamId]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-muted/30">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-4">
          <div>
            <Heading level={3}>Test Login</Heading>
            <Text size="sm" className="text-muted-foreground">
              Credential {credentialId} · Application {propertyId}
            </Text>
            <Text size="sm" className="mt-1 text-primary">
              {isStarting ? 'Starting login session…' : state.status}
            </Text>
          </div>
          <Flex gap="sm">
            <Button variant="outline" size="sm" onPress={handleBack}>
              Back to Credentials
            </Button>
          </Flex>
        </div>
      </header>
      <main className="mx-auto flex h-[calc(100vh-92px)] max-w-6xl flex-col gap-4 px-6 py-4">
        <section className="flex-1 overflow-hidden rounded-lg border border-border bg-card p-4">
          {error ? (
            <div className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-destructive-foreground">
              {error}
            </div>
          ) : isStarting || !streamId ? (
            <Flex align="center" justify="center" className="h-full py-12">
              <Flex direction="column" align="center" gap="sm">
                <LoadingOrbit size="lg" />
                <Text size="sm" className="text-muted-foreground">
                  Preparing browser stream…
                </Text>
              </Flex>
            </Flex>
          ) : (
            <BrowserStreamPanel state={state} layout="split" showActionHistory />
          )}
        </section>
      </main>
    </div>
  );
}
